import React, { useMemo, useRef, useState, useEffect } from 'react';
import s from './PromptBlock.module.css';
import CardItem from '../../../onboarding-page/entities/card-item/CardItem';
import { useSelector } from 'react-redux';
import { selectCurrentProject } from '../../../../store/selectors/projectSelectors';
import Button from '../../../../shared/ui/button/Button';
import { useAppDispatch } from '../../../../store/store';
import ToneSlider from '../tone-slider/ToneSlider';
import IconEmojiFormal from '../../../../shared/ui/icons/emodji/IconEmojiFormal';
import IconEmojiInformal from '../../../../shared/ui/icons/emodji/IconEmojiInformal';
import IconEmojiOptimistic from '../../../../shared/ui/icons/emodji/IconEmojiOptimistic';
import IconEmojiAssertive from '../../../../shared/ui/icons/emodji/IconEmojiAssertive';
import IconEmojiPlayful from '../../../../shared/ui/icons/emodji/IconEmojiPlayful';
import IconEmojiPersonal from '../../../../shared/ui/icons/emodji/IconEmojiPersonal';
import IconEmojiDefinitive from '../../../../shared/ui/icons/emodji/IconEmojiDefinitive';
import IconEmojiSarcastic from '../../../../shared/ui/icons/emodji/IconEmojiSarcastic';
import IconEmojiProfessional from '../../../../shared/ui/icons/emodji/IconEmojiProfessional';
import IconEmojiInspiring from '../../../../shared/ui/icons/emodji/IconEmojiInspiring';
import IconEmojiEmotional from '../../../../shared/ui/icons/emodji/IconEmojiEmotional';
import IconEmojiNeutral from '../../../../shared/ui/icons/emodji/IconEmojiNeutral';
import IconEmojiPersuasive from '../../../../shared/ui/icons/emodji/IconEmojiPersuasive';
import IconEmojiWitty from '../../../../shared/ui/icons/emodji/IconEmojiWitty';
import IconEmojiSerious from '../../../../shared/ui/icons/emodji/IconEmojiSerious';
import IconEmojiFun from '../../../../shared/ui/icons/emodji/IconEmojiFun';
import IconEmojiFriendly from '../../../../shared/ui/icons/emodji/IconEmojiFriendly';
import IconEmojiExciting from '../../../../shared/ui/icons/emodji/IconEmojiExciting';
import { generateContent, updateProject } from '../../../../shared/api/textProjectApi';
import { toast } from 'react-toastify';
const tones = [
    {
        text: 'Friendly',
        tag: 'friendly',
        icon: <IconEmojiFriendly />,
        sliderLabels: { negative: 'hostile', positive: 'friendly' },
    },
    {
        text: 'Exciting',
        tag: 'exciting',
        icon: <IconEmojiExciting />,
        sliderLabels: { negative: 'dull', positive: 'exciting' },
    },
    {
        text: 'Fun',
        tag: 'fun',
        icon: <IconEmojiFun />,
        sliderLabels: { negative: 'tedious', positive: 'fun' },
    },
    {
        text: 'Serious',
        tag: 'serious',
        icon: <IconEmojiSerious />,
        sliderLabels: { negative: 'lighthearted', positive: 'serious' },
    },
    {
        text: 'Witty',
        tag: 'witty',
        icon: <IconEmojiWitty />,
        sliderLabels: { negative: 'unimaginative', positive: 'witty' },
    },
    {
        text: 'Persuasive',
        tag: 'persuasive',
        icon: <IconEmojiPersuasive />,
        sliderLabels: { negative: 'ineffective', positive: 'persuasive' },
    },
    {
        text: 'Neutral',
        tag: 'neutral',
        icon: <IconEmojiNeutral />,
        sliderLabels: { negative: 'biased', positive: 'neutral' },
    },
    {
        text: 'Emotional',
        tag: 'emotional',
        icon: <IconEmojiEmotional />,
        sliderLabels: { negative: 'stoic', positive: 'emotional' },
    },
    {
        text: 'Formal',
        tag: 'formal',
        icon: <IconEmojiFormal />,
        sliderLabels: { negative: 'casual', positive: 'formal' },
    },
    {
        text: 'Inspiring',
        tag: 'inspiring',
        icon: <IconEmojiInspiring />,
        sliderLabels: { negative: 'discouraging', positive: 'inspiring' },
    },
    {
        text: 'Professional',
        tag: 'professional',
        icon: <IconEmojiProfessional />,
        sliderLabels: { negative: 'amateurish', positive: 'professional' },
    },
    {
        text: 'Sarcastic',
        tag: 'sarcastic',
        icon: <IconEmojiSarcastic />,
        sliderLabels: { negative: 'straightforward', positive: 'sarcastic' },
    },
    {
        text: 'Personal',
        tag: 'personal',
        icon: <IconEmojiPersonal />,
        sliderLabels: { negative: 'detached', positive: 'personal' },
    },
    {
        text: 'Authoritative',
        tag: 'authoritative',
        icon: <IconEmojiDefinitive />,
        sliderLabels: { negative: 'ambiguous', positive: 'authoritative' },
    },
    {
        text: 'Playful',
        tag: 'playful',
        icon: <IconEmojiPlayful />,
        sliderLabels: { negative: 'stern', positive: 'playful' },
    },
    {
        text: 'Assertive',
        tag: 'assertive',
        icon: <IconEmojiAssertive />,
        sliderLabels: { negative: 'submissive', positive: 'assertive' },
    },
    {
        text: 'Optimistic',
        tag: 'optimistic',
        icon: <IconEmojiOptimistic />,
        sliderLabels: { negative: 'pessimistic', positive: 'optimistic' },
    },
    {
        text: 'Informal',
        tag: 'informal',
        icon: <IconEmojiInformal />,
        sliderLabels: { negative: 'formal', positive: 'informal' },
    },
];
const ToneBlock = () => {
    const dispatch = useAppDispatch();
    const currentProject = useSelector(selectCurrentProject);

    const currentTones: { value: string; intensity: string }[] = currentProject?.tone || [];
    const getIntensityNumber = (toneValue: string, intensity: string) => {
        const toneMeta = tones.find((meta) => meta.tag === toneValue);
        if (!toneMeta) return 0;
        if (intensity === toneMeta.sliderLabels.negative) return -100;
        if (intensity === toneMeta.sliderLabels.positive) return 100;
        return 0;
    };

    const [selectedTones, setSelectedTones] = useState<{ value: string; intensity: number }[]>(
        currentTones.map((t) => ({
            value: t.value,
            intensity: getIntensityNumber(t.value, t.intensity),
        })),
    );

    const [activeSliderTone, setActiveSliderTone] = useState<string | null>(null);

    const handleOptionSelect = (tag: string) => {
        const found = selectedTones.find((t) => t.value === tag);
        if (!found) {
            if (selectedTones.length >= 3) {
                return;
            }
            setSelectedTones((prev) => [...prev, { value: tag, intensity: 0 }]);
            setActiveSliderTone(tag);
        } else if (activeSliderTone !== tag) {
            setActiveSliderTone(tag);
        } else {
            setSelectedTones((prev) => prev.filter((t) => t.value !== tag));
            setActiveSliderTone(null);
        }
    };

    const handleSliderChange = (tag: string, value: number) => {
        setSelectedTones((prev) =>
            prev.map((t) => (t.value === tag ? { ...t, intensity: value } : t)),
        );
    };

    const getIntensityString = (toneValue: string, intensity: number) => {
        const toneMeta = tones.find((meta) => meta.tag === toneValue);
        if (intensity < 0) return toneMeta?.sliderLabels.negative;
        if (intensity > 0) return toneMeta?.sliderLabels.positive;
        return 'neutral';
    };

    const hasChanges = useMemo(() => {
        console.log(selectedTones, currentTones);
        const selected = selectedTones
            .map((t) => t.value + ':' + getIntensityString(t.value, t.intensity))
            .sort()
            .join(',');
        const original = (currentTones || [])
            .map((t) => t.value + ':' + t.intensity)
            .sort()
            .join(',');

        console.log(selected, original);
        return selected !== original;
    }, [selectedTones, currentTones]);

    const updateTone = async () => {
        const tonesToSave = selectedTones.map((t) => {
            const toneMeta = tones.find((meta) => meta.tag === t.value);
            let intensity: string = 'neutral';
            if (t.intensity < 0) intensity = toneMeta?.sliderLabels.negative || 'negative';
            else if (t.intensity > 0) intensity = toneMeta?.sliderLabels.positive || 'positive';
            return { value: t.value, intensity };
        });
        await dispatch(updateProject({ ...currentProject, tone: tonesToSave }));
        await handleGenerateScript();
    };

    const handleGenerateScript = async () => {
        if (currentProject?.id) {
            try {
                const response = await dispatch(
                    generateContent({ projectId: currentProject.id, kind: 'text_only' }),
                ).unwrap();
                console.log('Script generated successfully:', response);
            } catch (error: any) {
                toast.error(error?.message || 'Error generating script');
                console.error('Failed to generate script:', error);
            }
        }
    };

    const scrollRef = useRef<HTMLDivElement>(null);
    const [currentPage, setCurrentPage] = useState(0);

    const tonesPerPage = 4; // 2x2 layout
    const groupedTones = useMemo(
        () =>
            Array.from({ length: Math.ceil(tones.length / tonesPerPage) }, (_, i) =>
                tones.slice(i * tonesPerPage, i * tonesPerPage + tonesPerPage),
            ),
        [],
    );

    const handleScroll = () => {
        if (scrollRef.current) {
            const scrollLeft = scrollRef.current.scrollLeft;
            const width = scrollRef.current.offsetWidth;
            const page = Math.round(scrollLeft / width);
            setCurrentPage(page);
        }
    };

    return (
        <div className={s.container}>
            <div className={s.block_container}>
                <div className={s.header_container}>
                    <h3 className={s.bold_header}>Adjust the tone of the text</h3>
                    <p className={s.text}>Choose how you want to wrap up your content</p>
                </div>

                {/* Desktop grid */}
                <div className={s.tone_options}>
                    {tones.map((toneItem) => (
                        <CardItem
                            key={toneItem.tag}
                            item={{
                                ...toneItem,
                                isActive: selectedTones.some((t) => t.value === toneItem.tag),
                            }}
                            onClick={() => handleOptionSelect(toneItem.tag)}
                            style={{
                                borderRadius: '5px',
                                fontSize: '10px',
                                height: '35px',
                                justifyContent: 'start',
                                padding: 5,
                                fontWeight: activeSliderTone === toneItem.tag ? 'bold' : 'normal',
                            }}
                        />
                    ))}
                </div>

                {/* Mobile carousel */}
                <div className={s.tone_carousel_wrapper}>
                    <div className={s.tone_carousel} ref={scrollRef} onScroll={handleScroll}>
                        {groupedTones.map((group, i) => (
                            <div className={s.tone_page} key={i}>
                                {group.map((toneItem) => (
                                    <CardItem
                                        key={toneItem.tag}
                                        item={{
                                            ...toneItem,
                                            isActive: selectedTones.some(
                                                (t) => t.value === toneItem.tag,
                                            ),
                                        }}
                                        onClick={() => handleOptionSelect(toneItem.tag)}
                                        style={{
                                            borderRadius: '5px',
                                            fontSize: '10px',
                                            height: '35px',
                                            justifyContent: 'start',
                                            padding: 5,
                                            fontWeight:
                                                activeSliderTone === toneItem.tag
                                                    ? 'bold'
                                                    : 'normal',
                                        }}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                    <div className={s.tone_dots}>
                        {groupedTones.map((_, i) => (
                            <div
                                key={i}
                                className={`${s.dot} ${i === currentPage ? s.active : ''}`}
                            />
                        ))}
                    </div>
                </div>

                {/* Slider and Confirm */}
                {activeSliderTone && (
                    <div className={s.slider_section}>
                        {(() => {
                            const toneItem = tones.find((t) => t.tag === activeSliderTone);
                            if (!toneItem) return null;
                            return (
                                <ToneSlider
                                    tone={activeSliderTone}
                                    value={
                                        selectedTones.find((t) => t.value === activeSliderTone)
                                            ?.intensity || 0
                                    }
                                    negativeLabel={toneItem.sliderLabels.negative}
                                    positiveLabel={toneItem.sliderLabels.positive}
                                    onChange={(value) =>
                                        handleSliderChange(activeSliderTone, value)
                                    }
                                />
                            );
                        })()}
                    </div>
                )}

                {hasChanges && (
                    <div style={{ margin: 7 }}>
                        <Button
                            onClick={updateTone}
                            text={'Confirm Selection'}
                            style={{ background: '#FFDD35', borderRadius: 4 }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ToneBlock;
